import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, mergeProps as _mergeProps, renderList as _renderList, createSlots as _createSlots } from "vue"

import { computed, ref, nextTick, onBeforeUnmount } from 'vue';
    import type { SelectInstance } from '@/interfaces/common';

    type SelectValue = unknown;

    type NormalizedItem = { title?: unknown; value?: unknown } | undefined;
    
export default /*@__PURE__*/_defineComponent({
  __name: 'AQSelect',
  props: {
    modelValue: {},
    fieldProps: {},
    label: {},
    items: {},
    itemTitle: {},
    itemValue: {},
    multiple: { type: Boolean },
    chips: { type: Boolean },
    prependInnerIcon: {},
    appendInnerIcon: {},
    returnObject: { type: Boolean },
    rules: {},
    disabled: { type: Boolean },
    readonly: { type: Boolean },
    required: { type: Boolean },
    enableInfinite: { type: Boolean },
    color: {},
    baseColor: {},
    hideDetails: { type: Boolean },
    clearable: { type: Boolean, default: false },
    closableChips: { type: Boolean },
    menuProps: {},
    loading: { type: Boolean }
  },
  emits: ["update:modelValue", "load-more"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const emit = __emit;

    const handleValueChange = (val: SelectValue | undefined) => {
        if (props.multiple) {
            emit('update:modelValue', Array.isArray(val) ? val : []);
        } else {
            emit('update:modelValue', val === undefined ? null : val);
        }
    };

    const chips = computed(() => {
        // if chips is explicitly set, use it; otherwise default to multiple mode (for better UX)
        return props.chips !== undefined ? props.chips : !!props.multiple;
    });

    const innerValue = computed({
        get: () => {
            if (props.multiple) {
                return Array.isArray(props.modelValue) ? props.modelValue : [];
            }
            return props.modelValue === undefined || props.modelValue === null
                ? null
                : props.modelValue;
        },
        set: handleValueChange,
    });

    const displayValue = computed({
        get: () => {
            if (!props.items.length) {
                return props.multiple ? [] : null;
            }
            return innerValue.value;
        },
        set: handleValueChange,
    });

    const clearableValue = computed(() => {
        if (props.clearable) {
            return Array.isArray(props.modelValue)
                ? props.modelValue.length > 0
                : !!props.modelValue;
        }
        return props.clearable;
    });

    const safeFieldProps = computed(() => props.fieldProps ?? {});
    const computedLabel = computed(() => {
        if (props.label) {
            return props.required ? `${props.label} *` : props.label;
        }
        return undefined;
    });
    const itemTitle = computed(() => props.itemTitle ?? 'title');
    const itemValue = computed(() => props.itemValue ?? 'value');
    const rules = computed(() => props.rules ?? []);
    const selectRef = ref<SelectInstance | null>(null);

    const getItemTitle = (it: NormalizedItem): string => {
        const t =
            it && typeof (it as { title?: unknown }).title !== 'undefined'
                ? (it as { title?: unknown }).title
                : '';
        return (t == null ? '' : String(t)).trim();
    };
    const hasTitle = (it: NormalizedItem): boolean => getItemTitle(it).length > 0;

    const removeItem = (index: number) => {
        if (props.multiple) {
            const current = Array.isArray(props.modelValue) ? props.modelValue : [];
            const next = current.filter((_, i: number) => i !== index);
            emit('update:modelValue', next as SelectValue);
        } else {
            emit('update:modelValue', null as SelectValue);
        }
    };

    /* --- ton code d’infinite scroll reste inchangé --- */
    let menuContentEl: HTMLElement | null = null;
    const handleScroll = () => {
        if (!props.enableInfinite || !menuContentEl) return;
        const threshold = 50;
        if (
            menuContentEl.scrollTop + menuContentEl.clientHeight >=
            menuContentEl.scrollHeight - threshold
        ) {
            emit('load-more');
        }
    };

    const findOverlayContent = (): HTMLElement | null => {
        const overlays = document.querySelectorAll<HTMLElement>(
            '.v-overlay__content, .v-overlay-content',
        );
        return overlays.length ? overlays[overlays.length - 1] : null;
    };

    const getScrollableElement = (overlay: HTMLElement): HTMLElement => {
        const inner = overlay.querySelector<HTMLElement>(
            '.v-virtual-scroll__content, .v-virtual-scroll, .v-list, [role="listbox"]',
        );
        return inner ?? overlay;
    };

    const attachScrollListener = () => {
        if (!props.enableInfinite) return;
        nextTick(() => {
            const overlay = findOverlayContent();
            if (!overlay) return;
            const scrollEl = getScrollableElement(overlay);
            scrollEl.addEventListener('scroll', handleScroll);
            menuContentEl = scrollEl;
        });
    };

    const detachScrollListener = () => {
        if (menuContentEl) {
            menuContentEl.removeEventListener('scroll', handleScroll);
            menuContentEl = null;
        }
    };

    const onMenuUpdate = (isOpen: boolean) => {
        if (isOpen) attachScrollListener();
        else detachScrollListener();
    };

    onBeforeUnmount(() => {
        detachScrollListener();
    });

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_select = _resolveComponent("v-select")!

  return (_openBlock(), _createBlock(_component_v_select, _mergeProps({
    ref_key: "selectRef",
    ref: selectRef,
    modelValue: displayValue.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((displayValue).value = $event))
  }, safeFieldProps.value, {
    label: computedLabel.value,
    variant: "outlined",
    items: props.items,
    "item-title": itemTitle.value,
    "item-value": itemValue.value,
    multiple: props.multiple,
    chips: chips.value,
    "closable-chips": props.closableChips,
    "prepend-inner-icon": props.prependInnerIcon,
    "hide-details": !!props.hideDetails,
    "append-inner-icon": props.appendInnerIcon,
    "return-object": props.returnObject,
    rules: rules.value,
    disabled: props.disabled,
    readonly: props.readonly,
    loading: props.loading,
    "onUpdate:menu": onMenuUpdate,
    rounded: "",
    color: props.color,
    "base-color": props.baseColor,
    required: props.required,
    clearable: clearableValue.value,
    "menu-props": props.menuProps,
    autocomplete: "off"
  }), _createSlots({ _: 2 }, [
    (props.chips)
      ? {
          name: "selection",
          fn: _withCtx(({ item, index }) => [
            (hasTitle(item))
              ? (_openBlock(), _createBlock(_component_v_chip, {
                  key: 0,
                  closable: "",
                  size: "small",
                  class: "ma-1",
                  "onClick:close": ($event: any) => (removeItem(index))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(getItemTitle(item)), 1)
                  ]),
                  _: 2
                }, 1032, ["onClick:close"]))
              : _createCommentVNode("", true)
          ]),
          key: "0"
        }
      : undefined,
    _renderList(_ctx.$slots, (_, slot) => {
      return {
        name: slot,
        fn: _withCtx((scope) => [
          _renderSlot(_ctx.$slots, slot, _normalizeProps(_guardReactiveProps(scope || {})))
        ])
      }
    })
  ]), 1040, ["modelValue", "label", "items", "item-title", "item-value", "multiple", "chips", "closable-chips", "prepend-inner-icon", "hide-details", "append-inner-icon", "return-object", "rules", "disabled", "readonly", "loading", "color", "base-color", "required", "clearable", "menu-props"]))
}
}

})